import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Tag } from 'lucide-react';
import { getCurrentLanguage } from '../utils/translations';
import api from '../components/api';
import Navbar from '../components/Navabar';
import Footer from '../components/Footer';

const InfoDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());

  // Récupérer l'élément depuis l'API
  useEffect(() => {
    const fetchItem = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/focus-items/');
        const foundItem = response.data.find(item => 
          item.slug === slug || item.id.toString() === slug
        );
        
        if (foundItem) {
          setItem(foundItem);
          setError(null);
        } else {
          setError('Élément non trouvé');
        }
      } catch (err) {
        console.error('Erreur lors du chargement de l\'élément:', err);
        setError('Erreur lors du chargement');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchItem();
    }
  }, [slug]);

  // Écouter les changements de langue
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLanguage(getCurrentLanguage());
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, []);

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#089556] mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !item) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto px-4">
            <h1 className="text-3xl font-bold text-gray-800 mb-4">Contenu non trouvé</h1>
            <p className="text-gray-600 mb-8">L'élément que vous recherchez n'existe pas.</p>
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center px-6 py-3 bg-[#089556] text-white rounded-lg hover:bg-[#077744] transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Retour
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Récupérer les données selon la langue
  const title = item.title || '';
  const description = currentLanguage === 'en' && item.description_en 
    ? item.description_en 
    : item.description_fr || '';
  const category = item.category_display || item.category || '';

  return (
    <>
      <Navbar />
      
      {/* Hero Section Simple et Professionnel */}
      <section className="relative bg-gradient-to-br from-[#089556] via-[#07D1EB] to-[#0ba8c7] py-16">
        <div className="absolute inset-0 bg-black/10"></div>
        
        <div className="max-w-5xl mx-auto px-4 relative z-10">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-white/80 text-sm mb-8">
            <button
              onClick={() => navigate(`/${currentLanguage}`)}
              className="hover:text-white transition-colors"
            >
              Accueil
            </button>
            <span>/</span>
            <button
              onClick={() => navigate(-1)}
              className="hover:text-white transition-colors"
            >
              En Focus
            </button>
            <span>/</span>
            <span className="text-white">{title}</span>
          </nav>

          {/* Titre et Catégorie */}
          <div className="text-white">
            {category && (
              <div className="mb-4">
                <span className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm text-white text-sm font-medium rounded-full">
                  <Tag className="w-4 h-4 mr-2" />
                  {category}
                </span>
              </div>
            )}

            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              {title}
            </h1>

            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center text-white/90 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </button>
          </div>
        </div>
      </section>

      {/* Image Principale */}
      {(item.image_url || item.image) && (
        <section className="bg-gray-50 py-8">
          <div className="max-w-4xl mx-auto px-4">
            <div className="rounded-xl overflow-hidden shadow-md bg-white">
              <img
                src={item.image_url || item.image}
                alt={title}
                className="w-full h-auto max-h-[500px] object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          </div>
        </section>
      )}

      {/* Contenu Principal */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <article className="prose prose-lg max-w-none">
            <div 
              className="text-gray-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </article>
        </div>
      </section>

      <Footer />

      <style jsx>{`
        .prose {
          color: #374151;
        }
        .prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6 {
          color: #1f2937;
          font-weight: 700;
          margin-top: 2rem;
          margin-bottom: 1rem;
        }
        .prose h2 {
          font-size: 1.875rem;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid #e5e7eb;
        }
        .prose h3 {
          font-size: 1.5rem;
        }
        .prose p {
          margin-bottom: 1.5rem;
          line-height: 1.8;
        }
        .prose ul, .prose ol {
          margin: 1.5rem 0;
          padding-left: 1.5rem;
        }
        .prose li {
          margin-bottom: 0.75rem;
        }
        .prose a {
          color: #089556;
          text-decoration: none;
          font-weight: 500;
          border-bottom: 1px solid #089556;
        }
        .prose a:hover {
          color: #077744;
          border-bottom-color: #077744;
        }
        .prose strong {
          color: #1f2937;
          font-weight: 600;
        }
        .prose blockquote {
          border-left: 4px solid #089556;
          padding-left: 1.5rem;
          margin: 2rem 0;
          font-style: italic;
          color: #4b5563;
        }
        .prose img {
          border-radius: 0.5rem;
          margin: 2rem 0;
        }
        .prose table {
          width: 100%;
          margin: 2rem 0;
        }
        .prose th {
          background: #f9fafb;
          padding: 0.75rem;
          text-align: left;
          font-weight: 600;
        }
        .prose td {
          padding: 0.75rem;
          border-bottom: 1px solid #e5e7eb;
        }
      `}</style>
    </>
  );
};

export default InfoDetail;
